import { Box, type SxProps, type Theme, Typography } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import type { ElementType, FC, ReactNode } from "react";
import { BADGE, FONT_SIZE, INK, LETTER_SPACING, LINE_HEIGHT, MOTION } from "@/theme/tokens";

type Props = {
	/** Small uppercase brand eyebrow above the title (already translated). Renders with a leading brand dot. */
	eyebrow?: ReactNode;
	/** The strong page/section title (already translated). */
	title: ReactNode;
	/**
	 * Semantic heading element for the title. Defaults to `h2` (a section heading) so several of these
	 * can safely coexist on one screen — e.g. tabs kept mounted via `display:none`. Opt into `h1` only
	 * for the single top-level page title. Purely semantic; the visual size is unchanged.
	 */
	titleComponent?: ElementType;
	/**
	 * Type scale for the title. `section` (default) is the working-surface size every existing caller
	 * gets; `hero` is one step up, for a detail page whose whole subject is a single entity (a candidate
	 * dossier, a record page) — at the section size that name reads as just another card heading on a
	 * page made of cards. Purely visual; `titleComponent` still owns the semantics.
	 */
	titleSize?: "section" | "hero";
	/**
	 * Rendered on the title's own line, right after it — a page's section tabs, say, which belong with the title on the
	 * left rather than with the actions on the right. Wraps under the title when the line runs out of room.
	 */
	titleAdornment?: ReactNode;
	/**
	 * Keeps the title as the page's heading for screen readers and heading navigation without drawing it — for a page
	 * whose visible identity is a control in `titleAdornment` (a report picker that shows the report's name), where
	 * the same name drawn again as text would say it twice. Off by default: the title shows.
	 */
	titleHidden?: boolean;
	/** Muted supporting line under the title (already translated). */
	subtitle?: ReactNode;
	/** Right-aligned action slot — typically one or more `SystemButton`s. Stays on the title's line from `md` up; wraps below it on narrow screens. */
	actions?: ReactNode;
	/**
	 * Minimum width (px) the title block keeps from `md` up, below which the `actions` wrap onto their own
	 * line. Opt-in, because the wrap breakpoint is a VIEWPORT query while the squeeze is a CONTAINER
	 * problem: a header inside a narrow column on a wide screen (a detail page beside a docked panel) is
	 * past `md`, so the actions stay on the title's line and crush the title to nothing. Set this to the
	 * width the title actually needs and the row wraps on container width instead. Omit it and the
	 * behavior is exactly as before.
	 */
	titleMinWidth?: number;
	/**
	 * `report` is a reporting page's quieter header, as the design system draws it: the title in the theme's headline
	 * face at its own weight, the eyebrow in brand-dark ink at semibold, a 13px muted subtitle, and the actions 8px apart.
	 * Omit it for `standard`, the header every page had before.
	 */
	variant?: "standard" | "report";
	dataTestId?: string;
	/**
	 * Annie walkthrough anchor on the root element. Declared because `Props` is closed — a hyphenated
	 * attribute passed from outside would be dropped without a type error, and the tour step would never render.
	 */
	"data-annie-target"?: string;
	sx?: SxProps<Theme>;
};

// Mount fade: opacity + a tiny lift (transform/opacity only → GPU), reduced-motion drops the movement.
const ENTRANCE_SX: SxProps<Theme> = {
	"@keyframes systemPageHeaderEnter": {
		from: { opacity: 0, transform: "translateY(6px)" },
		to: { opacity: 1, transform: "translateY(0)" },
	},
	animation: `systemPageHeaderEnter ${MOTION.DURATION.ENTRANCE_MD}ms ${MOTION.EASE_OUT}`,
	"@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

/**
 * The app-standard header for a page/section body: a small uppercase **brand eyebrow** (with a
 * leading brand dot), a strong **title**, a muted **subtitle**, and a right-aligned **actions**
 * slot that wraps beneath the title on narrow screens. Reach for this over a hand-rolled
 * title/subtitle/actions `Box` whenever a working surface (a data-table page, a config pane, a
 * detail view) needs an identity line with primary actions.
 *
 * Structure only — the eyebrow/title/subtitle are already-translated nodes and the actions are
 * caller-supplied; colors/type come from theme tokens so it stays one cohesive look everywhere.
 * Subtle mount fade, honors `prefers-reduced-motion`.
 */
export const SystemPageHeader: FC<Props> = ({
	eyebrow,
	title,
	titleComponent = "h2",
	titleSize = "section",
	titleAdornment,
	titleHidden,
	subtitle,
	actions,
	titleMinWidth,
	// DEFAULT: the header every page shipped with.
	variant = "standard",
	dataTestId,
	"data-annie-target": annieTarget,
	sx,
}) => {
	const report = variant === "report";
	const titleText = (
		<Typography
			component={titleComponent}
			sx={(theme) => ({
				fontSize: titleSize === "hero" ? { xs: FONT_SIZE.TITLE_XXL, md: FONT_SIZE.TITLE_HERO } : { xs: FONT_SIZE.TITLE_XL, md: FONT_SIZE.TITLE_XXL },
				color: "text.primary",
				...(report
					? { fontFamily: theme.typography.h1.fontFamily, fontWeight: theme.typography.h1.fontWeight, lineHeight: LINE_HEIGHT.TIGHT, mt: 0.5 }
					: { fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.2 }),
				// Last, so the type's own margin can't undo the hiding.
				...(titleHidden ? visuallyHidden : {}),
			})}
		>
			{title}
		</Typography>
	);
	return (
		<Box
			data-testid={dataTestId}
			data-annie-target={annieTarget}
			sx={[
				ENTRANCE_SX,
				{
					display: "flex",
					alignItems: "flex-end",
					justifyContent: "space-between",
					gap: 2,
					flexWrap: "wrap",
					rowGap: 1.5,
				},
				...(Array.isArray(sx) ? sx : [sx]),
			]}
		>
			<Box sx={{ minWidth: { xs: 0, md: titleMinWidth ?? 0 }, flex: { xs: "1 1 100%", md: "1 1 0" } }}>
				{eyebrow && (
					<Box sx={{ display: "flex", alignItems: "center", gap: report ? 1 : 0.875, mb: report ? 0 : 0.75 }}>
						<Box
							sx={{
								width: report ? BADGE.DOT_SIZE : BADGE.DOT_SIZE - 1,
								height: report ? BADGE.DOT_SIZE : BADGE.DOT_SIZE - 1,
								borderRadius: "50%",
								bgcolor: "primary.main",
								flexShrink: 0,
							}}
						/>
						<Typography
							component="span"
							sx={{
								fontSize: FONT_SIZE.LABEL_XS,
								textTransform: "uppercase",
								...(report
									? { fontWeight: 600, letterSpacing: LETTER_SPACING.LABEL, color: "primary.dark", lineHeight: 1.6 }
									: { fontWeight: 700, letterSpacing: "0.13em", color: "primary.main", lineHeight: 1 }),
							}}
						>
							{eyebrow}
						</Typography>
					</Box>
				)}
				{titleAdornment === undefined ? (
					titleText
				) : (
					<Box sx={{ display: "flex", alignItems: "center", columnGap: 2, rowGap: 1, flexWrap: "wrap", minWidth: 0 }}>
						{titleText}
						{titleAdornment}
					</Box>
				)}
				{subtitle && (
					<Typography
						variant="body2"
						sx={[{ color: "text.secondary", mt: 0.625, maxWidth: 640 }, report && { fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED, mt: 0.25 }]}
					>
						{subtitle}
					</Typography>
				)}
			</Box>
			{/* `maxWidth` is what lets `flexWrap` act: a non-shrinking box otherwise keeps its one-line width
			    and runs past the header's edge when the actions outgrow it, instead of wrapping them. */}
			{actions && (
				<Box
					sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: report ? 1 : 1.25, flexWrap: "wrap", flexShrink: 0, maxWidth: "100%" }}
				>
					{actions}
				</Box>
			)}
		</Box>
	);
};
